import { createContext, FC, ReactNode, useContext } from 'react';

import { CloseButton, Heading, Pressable } from '../../../shared/ui';
import { Settings } from '../../icons/settings';

/** Нижняя граница шторки: над панелью управления мобильного 1:1 (кнопки ~70px + safe-area). */
const SHEET_BOTTOM_CLASS = 'bottom-[calc(80px+env(safe-area-inset-bottom,0px))]';

export interface IMobileToolSheetApi {
  /** Развернуть шторку (например, когда пришёл результат клиента) */
  expand: () => void;
  /** Свернуть шторку в кнопку, чтобы видеть видео/поле инструмента */
  collapse: () => void;
  /** Закрыть текущий раунд и открыть инструмент заново */
  restart: () => void;
  /** Завершить инструмент (с подтверждением, если оно нужно) */
  finish: () => void;
}

export const MobileToolSheetContext = createContext<IMobileToolSheetApi | null>(null);

/** Доступ к шторке из панели инструмента; вне мобильной шторки возвращает null. */
export const useMobileToolSheet = () => useContext(MobileToolSheetContext);

interface IMobileToolSheetProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Высокая шторка (выбор колоды МАК, цветовой код): до 75% высоты экрана вместо 60% */
  tall?: boolean;
  /** Скрыть, но не размонтировать (инструмент хранит состояние внутри панели) */
  hidden?: boolean;
}

/** Мобильный 1:1: настройки инструмента в нижней шторке поверх видео/поля инструмента. */
export const MobileToolSheet: FC<IMobileToolSheetProps> = ({
  title,
  onClose,
  children,
  tall = false,
  hidden = false,
}) => (
  <div
    role="dialog"
    aria-label={title}
    className={`fixed inset-x-0 ${SHEET_BOTTOM_CLASS} z-40 ${hidden ? 'hidden' : 'flex'} ${
      tall ? 'max-h-[calc(var(--vh,1vh)*75)]' : 'max-h-[calc(var(--vh,1vh)*60)]'
    } flex-col rounded-t-card bg-ink-muted shadow-overlay`}
  >
    <div className="flex shrink-0 items-center justify-between px-4 pb-1 pt-3">
      <Heading size="md" tone="inverse">
        {title}
      </Heading>
      <CloseButton tone="inverse" onClick={onClose} />
    </div>
    <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{children}</div>
  </div>
);

interface IMobileToolSettingsFabProps {
  label: string;
  onClick: () => void;
  /** Сдвинуть правее, если слева уже стоит кнопка закрытия */
  afterCloseFab?: boolean;
}

/** Кнопка «Настройки» слева сверху, пока инструмент запущен и шторка свёрнута. */
export const MobileToolSettingsFab: FC<IMobileToolSettingsFabProps> = ({ label, onClick, afterCloseFab = false }) => (
  <Pressable
    onClick={onClick}
    aria-label={label}
    className={`fixed ${afterCloseFab ? 'left-[68px]' : 'left-3'} top-[calc(12px+env(safe-area-inset-top,0px))] z-40 rounded-control shadow-overlay`}
  >
    <Settings className="h-[44px] w-[44px]" />
  </Pressable>
);

interface IMobileToolCloseFabProps {
  label: string;
  onClick: () => void;
}

/** Кнопка «закрыть инструмент» слева сверху, когда колонка иконок скрыта, а панель инструмента не открыта. */
export const MobileToolCloseFab: FC<IMobileToolCloseFabProps> = ({ label, onClick }) => (
  // !size-11: кнопка над видео крупнее 32px кита — пальцем попадать удобнее
  <CloseButton
    label={label}
    tone="inverse"
    onClick={onClick}
    className="fixed left-3 top-[calc(12px+env(safe-area-inset-top,0px))] z-40 !size-11 bg-black/50 shadow-overlay"
  />
);
